import Swal from "sweetalert2";
import { apiV2, errorHandler } from "./index";
import {
  API_ERROR,
  CLEAR_CAPTIONS,
  FETCH_AUTHOR_CAPTION_ALIASES,
  FETCH_CAPTIONS_REVIEW_QUEUE,
  FETCH_CAPTION_VOCABULARY,
  FETCH_SESSION_CAPTIONS,
  FETCH_TRANSLATION_GLOSSARY,
  FETCH_TRANSLATION_INSTRUCTIONS,
  REMOVE_AUTHOR_CAPTION_ALIAS,
  REMOVE_CAPTION_VOCABULARY_TERM,
  REMOVE_SESSION_CAPTION,
  REMOVE_TRANSLATION_GLOSSARY_TERM,
  REMOVE_TRANSLATION_INSTRUCTION,
  SET_CAPTIONS_LOADING,
  UPDATE_SESSION_CAPTION,
  UPSERT_AUTHOR_CAPTION_ALIAS,
  UPSERT_CAPTION_VOCABULARY_TERM,
  UPSERT_TRANSLATION_GLOSSARY_TERM,
  UPSERT_TRANSLATION_INSTRUCTION,
} from "../constants/ActionTypes";

//= =================================================================
// Captions thunks — back the Captions Review Queue, per-session
// caption editor, and bulk-regenerate flows. Talks to the APIv2
// /admin/sessions/{id}/captions/* and /admin/captions/* endpoints
// introduced in Phase 3.
//= =================================================================

function setLoading(isLoading) {
  return { type: SET_CAPTIONS_LOADING, payload: isLoading };
}

/**
 * Render the API's 422 validation errors as a multi-line Swal. Uses the
 * `html` option (with `<br>` separators) because SweetAlert2's `text`
 * uses textContent under the hood and collapses newlines into spaces.
 * Each message is escaped before being concatenated so a server-side
 * error string can't inject HTML.
 */
function showVttValidationErrors(errors) {
  const escape = s =>
    String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  const html = errors
    .map(e => (e.line ? `Line ${escape(e.line)}: ${escape(e.message)}` : escape(e.message)))
    .join("<br>");
  return Swal.fire({ icon: "error", title: "VTT validation failed", html });
}

export function clearCaptions() {
  return { type: CLEAR_CAPTIONS };
}

/**
 * Paginated review queue listing. The API defaults to
 * status=auto_pending_review; callers can override via params.status.
 */
export function fetchCaptionsReviewQueue(params = {}) {
  return function (dispatch) {
    dispatch(setLoading(true));
    return apiV2
      .get(`/admin/captions/review-queue`, { params })
      .then(response => {
        dispatch({
          type: FETCH_CAPTIONS_REVIEW_QUEUE,
          payload: response.data,
        });
        return response;
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      })
      .finally(() => dispatch(setLoading(false)));
  };
}

/**
 * All caption rows (every language + state) for one session, with
 * inline flags. Used by the caption editor and the per-session panel.
 */
export function fetchSessionCaptions(sessionId) {
  return function (dispatch) {
    dispatch(setLoading(true));
    return apiV2
      .get(`/admin/sessions/${sessionId}/captions`)
      .then(response => {
        dispatch({
          type: FETCH_SESSION_CAPTIONS,
          payload: { sessionId, captions: response.data.objects ?? [] },
        });
        return response;
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      })
      .finally(() => dispatch(setLoading(false)));
  };
}

/**
 * Operator-triggered (re)generate. Server returns 202 and dispatches
 * the captions pipeline job; the caption row is reset to `processing`
 * and the reviewer will see it return to the queue once the job
 * completes.
 *
 * Options:
 * - `options.provider` (default `"assemblyai"`) — picks the
 *   transcription engine. AssemblyAI is the production winner as of
 *   api#545; the CMS no longer exposes any trigger for Whisper.
 *   Whisper still exists on the backend as a fallback and can be
 *   invoked via the env-var rollback path.
 * - `options.silent` (default `false`) — suppresses both the
 *   per-call success Swal AND the error toast. Used by the
 *   bulk-dispatch loop in `CaptionsSessionsListPage`, which shows
 *   a single aggregate summary at the end rather than flooding the
 *   screen with N modals. The rejection still propagates so the
 *   caller can count per-session failures.
 */
export function regenerateCaption(sessionId, language = "en", onSuccess, options = {}) {
  const provider = options.provider || "assemblyai";
  return function (dispatch) {
    return apiV2
      .post(`/admin/sessions/${sessionId}/captions/regenerate`, null, {
        params: { language, provider },
      })
      .then(response => {
        if (options.silent) {
          onSuccess?.();
        } else {
          Swal.fire({
            icon: "success",
            title: provider === "assemblyai" ? "AssemblyAI generation queued" : "Regeneration queued",
            text: "The caption will appear in the review queue when the job completes.",
            timer: 1500,
          }).then(() => onSuccess?.());
        }
        return response;
      })
      .catch(error => {
        if (!options.silent) {
          errorHandler(dispatch, error.response, API_ERROR);
        }
        throw error;
      });
  };
}

/**
 * Re-cue a single caption from its stored word sidecar — rebuild the
 * cues with the current segmentation logic without re-transcribing.
 * Only fires on not-yet-reviewed rows (server enforces). `source`
 * targets the specific track being edited.
 */
export function recueCaption(sessionId, language = "en", source, onSuccess, options = {}) {
  return function (dispatch) {
    return apiV2
      .post(`/admin/sessions/${sessionId}/captions/recue`, { language, source })
      .then(response => {
        const queued = response?.data?.queued ?? 0;
        if (!options.silent) {
          Swal.fire({
            icon: queued > 0 ? "success" : "info",
            title: queued > 0 ? "Re-cue queued" : "Nothing to re-cue",
            text:
              queued > 0
                ? "The captions will be rebuilt from the stored words and reappear when the job completes."
                : "Only not-yet-reviewed captions can be re-cued.",
            timer: queued > 0 ? 1600 : 2200,
          }).then(() => onSuccess?.());
        } else {
          onSuccess?.();
        }
        return response;
      })
      .catch(error => {
        if (!options.silent) {
          errorHandler(dispatch, error.response, API_ERROR);
        }
        throw error;
      });
  };
}

/**
 * Inline-editor save. `vtt` is the full WebVTT document. On success
 * the row flips to `human_edited` and is immediately servable.
 * Returns the resource so the caller can refresh local state.
 */
export function saveCaption(sessionId, language, vtt, onSuccess, source) {
  return function (dispatch) {
    return apiV2
      .put(
        `/admin/sessions/${sessionId}/captions/${language}`,
        { vtt },
        {
          params: source ? { source } : undefined,
        }
      )
      .then(response => {
        dispatch({
          type: UPDATE_SESSION_CAPTION,
          payload: { sessionId, language, caption: response.data },
        });
        Swal.fire({
          icon: "success",
          title: "Saved",
          timer: 1000,
        }).then(() => onSuccess?.(response.data));
        return response;
      })
      .catch(error => {
        // 422 returns line-level errors — surface them verbatim so the
        // operator can fix the offending cues without guessing.
        if (error?.response?.status === 422 && Array.isArray(error.response.data?.errors)) {
          showVttValidationErrors(error.response.data.errors);
          throw error;
        }
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

/**
 * Multipart upload — operator round-tripped the VTT through a desktop
 * tool. Same validation as saveCaption.
 */
export function uploadCaption(sessionId, language, file, onSuccess, source) {
  return function (dispatch) {
    const form = new FormData();
    form.append("vtt_file", file);
    return apiV2
      .post(`/admin/sessions/${sessionId}/captions/${language}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        params: source ? { source } : undefined,
      })
      .then(response => {
        dispatch({
          type: UPDATE_SESSION_CAPTION,
          payload: { sessionId, language, caption: response.data },
        });
        Swal.fire({
          icon: "success",
          title: "Uploaded",
          timer: 1000,
        }).then(() => onSuccess?.(response.data));
        return response;
      })
      .catch(error => {
        if (error?.response?.status === 422 && Array.isArray(error.response.data?.errors)) {
          showVttValidationErrors(error.response.data.errors);
          throw error;
        }
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

/**
 * Approve the auto-generated caption as-is. Returns 409 if the row
 * isn't in `auto_pending_review` (handled here as a friendly toast).
 */
export function approveCaption(sessionId, language, onSuccess, source) {
  return function (dispatch) {
    return apiV2
      .post(`/admin/sessions/${sessionId}/captions/${language}/approve`, null, {
        params: source ? { source } : undefined,
      })
      .then(response => {
        dispatch({
          type: UPDATE_SESSION_CAPTION,
          payload: { sessionId, language, caption: response.data },
        });
        Swal.fire({
          icon: "success",
          title: "Approved",
          timer: 1000,
        }).then(() => onSuccess?.(response.data));
        return response;
      })
      .catch(error => {
        if (error?.response?.status === 409) {
          Swal.fire({
            icon: "warning",
            title: "Cannot approve",
            text: error.response.data?.message || "Only captions awaiting review can be approved.",
          });
          throw error;
        }
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

export function deleteCaption(sessionId, language, onSuccess, source) {
  return function (dispatch) {
    return apiV2
      .delete(`/admin/sessions/${sessionId}/captions/${language}`, {
        params: source ? { source } : undefined,
      })
      .then(response => {
        dispatch({
          type: REMOVE_SESSION_CAPTION,
          payload: { sessionId, language, source },
        });
        Swal.fire({
          icon: "success",
          title: "Caption deleted",
          timer: 1000,
        }).then(() => onSuccess?.());
        return response;
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

/**
 * Download the raw VTT as an attachment. Returns the Blob so the caller
 * can trigger a save-to-disk dialog in the browser. The endpoint sets
 * Content-Disposition: attachment server-side.
 */
export function downloadCaption(sessionId, language, source) {
  return function (dispatch) {
    return apiV2
      .get(`/admin/sessions/${sessionId}/captions/${language}/download`, {
        responseType: "blob",
        params: source ? { source } : undefined,
      })
      .then(response => response)
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

//= =================================================================
// Caption vocabulary (Phase 4.5) — talks to /admin/captions/vocabulary
// and /admin/authors/{authorId}/caption-aliases. Kelly uses these to
// keep the prompt bias + post-processor name fixes current.
//= =================================================================

export function fetchCaptionVocabulary(params = {}) {
  return function (dispatch) {
    // Note: deliberately NOT dispatching setLoading() — that flips the
    // global state.captions.isLoading flag which is shared with the
    // CaptionsReviewQueuePage. The Vocabulary page gates its own
    // backdrop on `vocabulary === null` and doesn't need the global
    // flag; mutating it here would flicker the queue page's spinner
    // for users with both pages in their tab back-stack.
    return apiV2
      .get("/admin/captions/vocabulary", { params })
      .then(response => {
        dispatch({
          type: FETCH_CAPTION_VOCABULARY,
          payload: response.data.objects ?? [],
        });
        return response;
      })
      .catch(error => {
        // Flip vocabulary off the `null` loading sentinel so the page
        // exits the backdrop and renders the empty state — otherwise a
        // failed fetch would leave LoadingBackdrop covering the page
        // forever with no way to recover. The error toast still fires.
        dispatch({ type: FETCH_CAPTION_VOCABULARY, payload: [] });
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

/**
 * Pass `{ silent: true }` to suppress the per-call SUCCESS Swal only —
 * errors still surface via `errorHandler` so the user always learns
 * when a save fails. Used by the inline active toggle in the
 * Vocabulary page, which would otherwise pop a blocking modal on
 * every flip.
 */
export function saveCaptionVocabularyTerm(term, onSuccess, options = {}) {
  return function (dispatch) {
    const isEdit = !!term.id;
    const request = isEdit
      ? apiV2.put(`/admin/captions/vocabulary/${term.id}`, term)
      : apiV2.post("/admin/captions/vocabulary", term);

    return request
      .then(response => {
        dispatch({ type: UPSERT_CAPTION_VOCABULARY_TERM, payload: response.data });
        if (options.silent) {
          onSuccess?.(response.data);
        } else {
          Swal.fire({ icon: "success", title: isEdit ? "Updated" : "Added", timer: 900 }).then(() =>
            onSuccess?.(response.data)
          );
        }
        return response;
      })
      .catch(error => {
        // Always surface errors — `silent` only controls the success
        // modal, not error feedback. A failed toggle previously
        // appeared as an unresponsive Switch with no explanation.
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

/**
 * Soft delete (deactivate) by default — the API flips is_active=false
 * which removes the term from prompt/post-processor rotation without
 * losing the row. Pass `{ force: true }` to hard-delete.
 *
 * Signature mirrors saveCaptionVocabularyTerm + regenerateCaption:
 * (id, onSuccess, options) — keeping `onSuccess` in the same slot
 * across sibling thunks so callers don't silently mis-pass a callback
 * as an options bag.
 */
export function deleteCaptionVocabularyTerm(id, onSuccess, options = {}) {
  return function (dispatch) {
    const url = options.force ? `/admin/captions/vocabulary/${id}?force=1` : `/admin/captions/vocabulary/${id}`;
    return apiV2
      .delete(url)
      .then(response => {
        if (options.force) {
          dispatch({ type: REMOVE_CAPTION_VOCABULARY_TERM, payload: id });
        } else {
          dispatch({ type: UPSERT_CAPTION_VOCABULARY_TERM, payload: response.data });
        }
        Swal.fire({ icon: "success", title: options.force ? "Deleted" : "Deactivated", timer: 900 }).then(() =>
          onSuccess?.()
        );
        return response;
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

export function fetchAuthorCaptionAliases(authorId) {
  return function (dispatch) {
    return apiV2
      .get(`/admin/authors/${authorId}/caption-aliases`)
      .then(response => {
        dispatch({
          type: FETCH_AUTHOR_CAPTION_ALIASES,
          payload: { authorId, aliases: response.data.objects ?? [] },
        });
        return response;
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

export function saveAuthorCaptionAlias(authorId, alias, onSuccess) {
  return function (dispatch) {
    const isEdit = !!alias.id;
    const request = isEdit
      ? apiV2.put(`/admin/authors/${authorId}/caption-aliases/${alias.id}`, alias)
      : apiV2.post(`/admin/authors/${authorId}/caption-aliases`, alias);

    return request
      .then(response => {
        dispatch({ type: UPSERT_AUTHOR_CAPTION_ALIAS, payload: { authorId, alias: response.data } });
        Swal.fire({ icon: "success", title: isEdit ? "Updated" : "Added", timer: 900 }).then(() =>
          onSuccess?.(response.data)
        );
        return response;
      })
      .catch(error => {
        // 409 = (author_id, misheard) unique conflict — surface a clear toast.
        if (error?.response?.status === 409) {
          Swal.fire({
            icon: "warning",
            title: "Alias already exists",
            text: error.response.data?.message || "This misheard form is already mapped for this author.",
          });
          throw error;
        }
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

export function deleteAuthorCaptionAlias(authorId, id, onSuccess) {
  return function (dispatch) {
    return apiV2
      .delete(`/admin/authors/${authorId}/caption-aliases/${id}`)
      .then(response => {
        dispatch({ type: REMOVE_AUTHOR_CAPTION_ALIAS, payload: { authorId, id } });
        Swal.fire({ icon: "success", title: "Removed", timer: 900 }).then(() => onSuccess?.());
        return response;
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

//= =================================================================
// Translation glossary + instructions (Phase 5) — talk to
// /admin/captions/translation-glossary and
// /admin/captions/translation-instructions. Both are language-scoped
// via a `language` query param the CMS page passes from its picker.
//= =================================================================

export function fetchTranslationGlossary(params = {}) {
  return function (dispatch) {
    return apiV2
      .get("/admin/captions/translation-glossary", { params })
      .then(response => {
        dispatch({ type: FETCH_TRANSLATION_GLOSSARY, payload: response.data.objects ?? [] });
        return response;
      })
      .catch(error => {
        // Match the vocabulary thunk's recovery pattern — flip off the
        // `null` sentinel on failure so the page exits the backdrop
        // and renders the empty state. errorHandler still toasts.
        dispatch({ type: FETCH_TRANSLATION_GLOSSARY, payload: [] });
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

/**
 * Pass `{ silent: true }` to suppress the success Swal — useful for an
 * inline toggle. Errors always surface via errorHandler regardless of
 * silent.
 */
export function saveTranslationGlossaryTerm(term, onSuccess, options = {}) {
  return function (dispatch) {
    const isEdit = !!term.id;
    const request = isEdit
      ? apiV2.put(`/admin/captions/translation-glossary/${term.id}`, term)
      : apiV2.post("/admin/captions/translation-glossary", term);
    return request
      .then(response => {
        dispatch({ type: UPSERT_TRANSLATION_GLOSSARY_TERM, payload: response.data });
        if (options.silent) {
          onSuccess?.(response.data);
        } else {
          Swal.fire({ icon: "success", title: isEdit ? "Updated" : "Added", timer: 900 }).then(() =>
            onSuccess?.(response.data)
          );
        }
        return response;
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

/**
 * Soft delete (deactivate) by default; pass `{ force: true }` to
 * hard-delete. Signature mirrors deleteCaptionVocabularyTerm —
 * (id, onSuccess, options) — for cross-thunk consistency.
 */
export function deleteTranslationGlossaryTerm(id, onSuccess, options = {}) {
  return function (dispatch) {
    const url = options.force
      ? `/admin/captions/translation-glossary/${id}?force=1`
      : `/admin/captions/translation-glossary/${id}`;
    return apiV2
      .delete(url)
      .then(response => {
        if (options.force) {
          dispatch({ type: REMOVE_TRANSLATION_GLOSSARY_TERM, payload: id });
        } else {
          dispatch({ type: UPSERT_TRANSLATION_GLOSSARY_TERM, payload: response.data });
        }
        onSuccess?.();
        return response;
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

export function fetchTranslationInstructions(params = {}) {
  return function (dispatch) {
    return apiV2
      .get("/admin/captions/translation-instructions", { params })
      .then(response => {
        dispatch({ type: FETCH_TRANSLATION_INSTRUCTIONS, payload: response.data.objects ?? [] });
        return response;
      })
      .catch(error => {
        dispatch({ type: FETCH_TRANSLATION_INSTRUCTIONS, payload: [] });
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

export function saveTranslationInstruction(instruction, onSuccess, options = {}) {
  return function (dispatch) {
    const isEdit = !!instruction.id;
    const request = isEdit
      ? apiV2.put(`/admin/captions/translation-instructions/${instruction.id}`, instruction)
      : apiV2.post("/admin/captions/translation-instructions", instruction);
    return request
      .then(response => {
        dispatch({ type: UPSERT_TRANSLATION_INSTRUCTION, payload: response.data });
        if (options.silent) {
          onSuccess?.(response.data);
        } else {
          Swal.fire({ icon: "success", title: isEdit ? "Updated" : "Added", timer: 900 }).then(() =>
            onSuccess?.(response.data)
          );
        }
        return response;
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

export function deleteTranslationInstruction(id, onSuccess, options = {}) {
  return function (dispatch) {
    const url = options.force
      ? `/admin/captions/translation-instructions/${id}?force=1`
      : `/admin/captions/translation-instructions/${id}`;
    return apiV2
      .delete(url)
      .then(response => {
        if (options.force) {
          dispatch({ type: REMOVE_TRANSLATION_INSTRUCTION, payload: id });
        } else {
          dispatch({ type: UPSERT_TRANSLATION_INSTRUCTION, payload: response.data });
        }
        onSuccess?.();
        return response;
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

/**
 * Operator-initiated translate. Fires the backend's translate endpoint
 * which dispatches the queued TranslateCaptionJob. Backend returns 202;
 * caller is responsible for any "queued" feedback and for refreshing
 * the session's caption list afterwards. Errors (e.g. source not
 * approved → 409, missing source → 404) surface via errorHandler.
 */
export function translateSessionCaption(sessionId, fromLanguage, toLanguage, onSuccess) {
  return function (dispatch) {
    return apiV2
      .post(`/admin/sessions/${sessionId}/captions/translate`, null, {
        params: { from: fromLanguage, to: toLanguage },
      })
      .then(response => {
        onSuccess?.(response.data);
        return response;
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

// ──────────────────────── Bulk dispatch actions ────────────────────────
// Three thunks calling the bulk endpoints added in APIv2
// release-2026-05-19. Each does ONE HTTP request that fans out to N
// queue jobs server-side — replaces the N-requests-per-batch pattern
// the old CaptionsSessionsListPage used, which was tripping the
// throttle:api rate limit on backfills of hundreds of sessions.
//
// Each returns a Promise resolving to the response payload so the
// caller can render its own summary modal (counts, skipped reasons,
// etc.) — none of these fire a Swal themselves.

/**
 * Bulk-dispatch transcription generation for an array of session IDs.
 * Server returns { queued, skipped, language, provider, details: [...] }.
 */
export function bulkRegenerateCaptions(sessionIds, options = {}) {
  const language = options.language || "en";
  const provider = options.provider || "assemblyai";
  return function (dispatch) {
    return apiV2
      .post("/admin/captions/regenerate-bulk", {
        session_ids: sessionIds,
        language,
        provider,
      })
      .then(response => response.data)
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

/**
 * Bulk-dispatch translation for an array of session IDs into the
 * given target language. Sessions without an approved source caption
 * come back in `skipped` with reason="no_approved_source" so the
 * caller can render a clear "X queued, Y need EN approval first"
 * summary. Returns { queued, skipped, from, to, details: [...] }.
 */
export function bulkTranslateCaptions(sessionIds, to, options = {}) {
  const from = options.from || "en";
  return function (dispatch) {
    return apiV2
      .post("/admin/captions/translate-bulk", {
        session_ids: sessionIds,
        from,
        to,
      })
      .then(response => response.data)
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

/**
 * Fetch every caption row matching `status` (or all if "all"), across
 * all pages, returning the `session_caption_id` list only. Used by
 * the Review Queue's "Select all N matching" button so an operator
 * can grab every row of a status (typically auto_pending_review)
 * without paginating through the table picking page-by-page.
 *
 * Does NOT dispatch into Redux — pure read, returns a Promise<int[]>.
 * We send a deliberately large `per_page` so the response is
 * single-page; if the real backlog ever exceeds it the operator gets
 * a clear "first N captured" message and can iterate.
 */
export function fetchReviewQueueCaptionIds(status = "auto_pending_review", search = "", limit = 10000) {
  return function (dispatch) {
    return apiV2
      .get("/admin/captions/review-queue", {
        params: { status, page: 1, per_page: limit, ...(search ? { search } : {}) },
      })
      .then(response => {
        const rows = response.data?.data ?? [];
        return {
          ids: rows.map(r => r.session_caption_id).filter(Boolean),
          totalCount: response.data?.total_count ?? rows.length,
          requestedLimit: limit,
        };
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

/**
 * Bulk re-apply deterministic vocab transforms (author aliases,
 * brand-term capitalization, author-name-unmatched check) to existing
 * captions for the given session IDs. Cheap follow-up to a vocab
 * edit — does NOT re-transcribe audio. Status preserved per row.
 * Returns { updated, skipped, details: [...] }.
 */
export function bulkReapplyVocabulary(sessionIds) {
  return function (dispatch) {
    return apiV2
      .post("/admin/captions/reapply-vocab-bulk", {
        session_ids: sessionIds,
      })
      .then(response => response.data)
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

/**
 * Bulk re-cue: rebuild caption cues from the stored word sidecars using
 * the current segmentation logic — does NOT re-transcribe audio. Queues
 * one async job per eligible row. Only not-yet-reviewed rows are touched
 * (human-reviewed / human-edited are skipped server-side). Returns
 * { queued, skipped, details: [...] }.
 */
export function bulkRecueCaptions(sessionIds) {
  return function (dispatch) {
    return apiV2
      .post("/admin/captions/recue-bulk", {
        session_ids: sessionIds,
      })
      .then(response => response.data)
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}

/**
 * Bulk-approve an array of caption row IDs (session_caption_id —
 * NOT session_id). The Review Queue surface already lists rows by
 * caption_id, so the client always has these handy. Rows not in
 * `auto_pending_review` come back in `skipped`. Returns
 * { approved, skipped, details: [...] }.
 */
export function bulkApproveCaptions(captionIds) {
  return function (dispatch) {
    return apiV2
      .post("/admin/captions/approve-bulk", {
        caption_ids: captionIds,
      })
      .then(response => response.data)
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        throw error;
      });
  };
}
